import { Grid, Typography, useTheme } from '@mui/material';
import { parseISO } from 'date-fns';
import React, { PropsWithChildren } from 'react';
import {
  formatUTCDate,
  getAge,
  ProfileCardHeader,
  LabelBox,
  ProfileCardPaper,
  getTimezoneString,
  maskPhone,
  formatDate,
} from '@fdha/web-ui-library';
import { useGetSupportedLanguagesQuery } from '@fdha/graphql-api-admin';

import { formatProfileAttribute } from '../../utils/accountInfoData';
import { getStatusLabel } from '../../utils';

interface ProfileCardProps {
  name: string;
  subtitle?: string;
  email: string;
  phoneNumber?: string | null;
}

export const ProfileCard: React.FC<PropsWithChildren<ProfileCardProps>> = ({
  name,
  subtitle,
  email,
  phoneNumber,
  children,
}) => {
  const emailLabelBoxWidth = phoneNumber ? 6 : 12;
  return (
    <ProfileCardPaper sx={{ paddingX: 3, paddingY: 4 }}>
      <ProfileCardHeader title={name} subtitle={subtitle} />
      <Grid container spacing={4}>
        {phoneNumber && (
          <LabelBox
            size={6}
            label="Phone number"
            value={maskPhone(phoneNumber, false)}
            hideMargin
          />
        )}
        <LabelBox
          size={emailLabelBoxWidth}
          label="Email"
          value={email}
          hideMargin
        />
        {children}
      </Grid>
    </ProfileCardPaper>
  );
};

type PatientSpecificInfoProps = {
  isCT?: boolean;
  birthDate?: string;
  cancerType?: string;
  diagnosisDate?: string;
  height?: number;
  weight?: number;
  joinedOn?: string;
  timezone?: string;
  restrictAccess?: boolean;
  showSiteFeatures?: boolean;
  preferredLanguage?: string;
  lastLogin?: string | null;
};

export const PatientSpecificInfo: React.FC<PatientSpecificInfoProps> = ({
  isCT,
  birthDate,
  height,
  weight,
  joinedOn,
  timezone,
  restrictAccess,
  showSiteFeatures,
  preferredLanguage,
  lastLogin,
}) => {
  const theme = useTheme();

  const { data: languagesData } = useGetSupportedLanguagesQuery();

  const supportedLanguages = languagesData?.supportedLanguages;

  const getLanguageName = (language: string | undefined) => {
    return supportedLanguages?.find((lang) => lang.code === language)
      ?.nameEnglish;
  };

  const getLastLoginValue = (value?: string | null) => {
    if (value) {
      return value;
    }

    return (
      <Typography color={theme.palette.text.secondary}>
        No login activity
      </Typography>
    );
  };

  return (
    <>
      {!restrictAccess && (
        <>
          <LabelBox
            size={6}
            label="Birth date"
            value={formatUTCDate(birthDate)}
            hideMargin
          />
          <LabelBox
            size={6}
            label="Age"
            value={formatProfileAttribute('AGE', getAge(birthDate))}
            hideMargin
          />
          {showSiteFeatures && isCT && (
            <>
              <LabelBox
                size={6}
                label="Weight"
                value={formatProfileAttribute('WEIGHT', weight)}
                hideMargin
              />
              <LabelBox
                size={6}
                label="Height"
                value={formatProfileAttribute('HEIGHT', height)}
                hideMargin
              />
            </>
          )}
        </>
      )}
      {((isCT && !showSiteFeatures) || !isCT) && (
        <>
          <LabelBox
            size={6}
            label="Joined on"
            value={joinedOn ? formatDate(parseISO(joinedOn)) : undefined}
            hideMargin
          />
        </>
      )}
      <LabelBox
        size={6}
        label="Timezone"
        value={getTimezoneString(timezone)}
        hideMargin
      />
      {isCT && (
        <LabelBox
          size={6}
          label="Preferred language"
          value={getLanguageName(preferredLanguage)}
          hideMargin
        />
      )}

      <LabelBox
        size={12}
        label="Last Login"
        value={getLastLoginValue(lastLogin)}
        hideMargin
      />
    </>
  );
};

interface FoodOpsSpecificInfoProps {
  joinedOn?: string;
}

export const FoodOpsSpecificInfo: React.FC<FoodOpsSpecificInfoProps> = ({
  joinedOn,
}) => {
  return (
    <LabelBox
      size={6}
      label="Joined on"
      value={joinedOn ? formatDate(parseISO(joinedOn)) : undefined}
      hideMargin
    />
  );
};

interface SiteStaffSpecificInfoProps {
  associatedSite: string;
  isActive: boolean;
  joinedOn?: string;
  role: string;
}

export const SiteStaffSpecificInfo: React.FC<SiteStaffSpecificInfoProps> = ({
  associatedSite,
  isActive,
  joinedOn,
  role,
}) => {
  return (
    <>
      <LabelBox
        size={6}
        label="Joined on"
        value={joinedOn ? formatDate(parseISO(joinedOn)) : undefined}
        hideMargin
      />
      <LabelBox
        size={6}
        label="Associated site"
        value={associatedSite}
        hideMargin
      />
      <LabelBox size={6} label="Staff role" value={role} hideMargin />
      <LabelBox
        size={6}
        label="Site staff status"
        value={getStatusLabel(isActive)}
      />
    </>
  );
};
